import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState, type ReactNode } from 'react';
import { useApi } from '../api/context';
import { useToast } from '../components/toast';
import { strings } from '../strings';
import { RevealConfirm, type RevealChoice } from './RevealConfirm';
import {
  copyFailure,
  isReasonRequired,
  isReasonRequiredError,
  markReasonRequired,
  needsConfirmation,
  reasonProblems,
  reasonToSend,
  skipConfirmation,
  writeClipboard,
  type RevealEnvironment,
  type RevealLocation,
} from './reveal';

/** What Copy value copies: a secret that can be revealed. */
export interface CopyTarget {
  secretName: string;
  /** The name messages use. */
  display: string;
  location: RevealLocation;
}

interface Pending {
  /** The deployment the copy was asked for; the confirmation is dropped when the window shows another. */
  deploymentId: string;
  target: CopyTarget;
  anchor: HTMLElement | null;
  problems: string[];
  reason: string;
}

/**
 * Copy value (spec 6.10.18): the confirmation when it is due, then the request with `purpose: "copy"` and the
 * clipboard write in the same user gesture. The value is never rendered.
 */
export function useRevealCopy(env: RevealEnvironment | null): {
  copy: (target: CopyTarget, anchor: HTMLElement | null) => void;
  popover: ReactNode;
} {
  const api = useApi();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<Pending | null>(null);
  const [busy, setBusy] = useState(false);
  if (pending && pending.deploymentId !== env?.deploymentId) setPending(null);

  // Runs inside the click or key event that confirmed it, so the browser lets the clipboard be written.
  const send = useCallback(
    (target: CopyTarget, anchor: HTMLElement | null, reason: string) => {
      if (!env) return;
      const deploymentId = env.deploymentId;
      setBusy(true);
      const request = api.revealSecret(deploymentId, target.secretName, {
        purpose: 'copy',
        reason: reasonToSend(reason),
      });
      const outcome = request.then(
        () => null,
        (error: unknown) => error,
      );
      writeClipboard(request.then((secret) => secret.value))
        .then(
          () => {
            setPending(null);
            toast.show({ tone: 'success', title: strings.reveal.copiedRecorded });
          },
          async (clipboardError: unknown) => {
            const error = (await outcome) ?? clipboardError;
            if (isReasonRequiredError(error)) markReasonRequired(env.user);
            const problems = reasonProblems(error);
            if (problems.length) {
              setPending({ deploymentId, target, anchor, problems, reason });
              return;
            }
            setPending(null);
            toast.show({ tone: 'error', ...copyFailure(error) });
          },
        )
        .finally(() => {
          setBusy(false);
          void queryClient.invalidateQueries({ queryKey: ['deployment', deploymentId, 'activity'] });
        });
    },
    [api, env, queryClient, toast],
  );

  const copy = useCallback(
    (target: CopyTarget, anchor: HTMLElement | null) => {
      if (!env?.canReveal) return;
      if (needsConfirmation(env.user))
        setPending({ deploymentId: env.deploymentId, target, anchor, problems: [], reason: '' });
      else send(target, anchor, '');
    },
    [env, send],
  );

  const confirmed = (choice: RevealChoice) => {
    if (!pending || !env || pending.deploymentId !== env.deploymentId) return;
    if (choice.dontAsk) skipConfirmation(env.user);
    send(pending.target, pending.anchor, choice.reason);
  };

  const popover =
    pending && env && pending.deploymentId === env.deploymentId ? (
      <RevealConfirm
        key={`${pending.target.secretName}|${pending.problems.join('|')}`}
        name={pending.target.display}
        location={pending.target.location}
        account={env.account}
        purpose="copy"
        anchor={pending.anchor}
        required={isReasonRequired()}
        problems={pending.problems}
        busy={busy}
        initialReason={pending.reason}
        onConfirm={confirmed}
        onCancel={() => setPending(null)}
      />
    ) : null;

  return { copy, popover };
}
